import { Link } from "react-router-dom";
import { motion } from "framer-motion";

/**
 * Premium reusable course/exam card.
 * Props: title, tag, desc, icon (fa slug), brandIcon (bool), gradient (optional tailwind gradient class),
 *        enquireHref (default /contact), learnMoreHref (default /contact), delay, className
 */
export default function CourseCard({
  title,
  tag,
  desc,
  icon = "book-open",
  brandIcon = false,
  gradient = "from-brand-primary to-brand-dark",
  enquireHref = "/contact",
  learnMoreHref = "/contact",
  delay = 0,
  className = "",
  testId,
}) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className={`group relative flex flex-col h-full rounded-2xl border border-brand-line bg-white p-6 sm:p-7 shadow-[0_15px_40px_-25px_rgba(78,52,46,.35)] hover:shadow-[0_30px_70px_-30px_rgba(212,175,55,.45)] transition-shadow overflow-hidden ${className}`}
      data-testid={testId}
    >
      {/* Decorative gold glow */}
      <div className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 rounded-full bg-brand-gold/10 blur-2xl group-hover:bg-brand-gold/25 transition-colors duration-500" />

      <div className="relative flex items-center justify-between">
        <span className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} text-brand-gold grid place-items-center shadow-lg`}>
          <i className={`${brandIcon ? "fa-brands" : "fa-solid"} fa-${icon} text-xl`} />
        </span>
        {tag && (
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-brand-primary bg-brand-gold/15 border border-brand-gold/40 rounded-full px-3 py-1">
            {tag}
          </span>
        )}
      </div>

      <h3 className="mt-5 display-serif text-xl sm:text-2xl font-bold text-brand-dark leading-tight">{title}</h3>
      {desc && <p className="mt-2 text-sm text-brand-mute leading-relaxed flex-1">{desc}</p>}

      <div className="mt-5 flex flex-wrap gap-2">
        <Link
          to={learnMoreHref}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white transition-all"
          data-testid={testId ? `${testId}-learn` : undefined}
        >
          Learn More <i className="fa-solid fa-arrow-right text-[10px]" />
        </Link>
        <Link
          to={enquireHref}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold bg-brand-primary text-white hover:bg-brand-gold hover:text-brand-dark transition-all shadow-md"
          data-testid={testId ? `${testId}-enquire` : undefined}
        >
          Enquire Now <i className="fa-solid fa-paper-plane text-[10px]" />
        </Link>
      </div>
    </motion.article>
  );
}
